import React from "react";
import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useMaterialColor } from "hooks/useMaterials";
import { useMassFormatter } from "hooks/useMassFormatter";
import { useTenantTranslation } from "hooks/formatters";
import { sorted } from "helpers";
import { DisplayLayer } from "./layeringComparison";

interface ProportionalLayerStackProps {
  title: string;
  layers: DisplayLayer[];
  scaleMass: number;
  containerHeight: number;
}

const INLINE_LABEL_MIN_HEIGHT = 14;
// Equal-width stripes for a dual-material layer: the split of the physical mass
// between the two materials is an estimate, so it is not encoded visually.
const STRIPE_WIDTH_PX = 8;

export const ProportionalLayerStack: React.FC<ProportionalLayerStackProps> = ({
  title,
  layers,
  scaleMass,
  containerHeight,
}) => {
  const theme = useTheme();
  const { getColor } = useMaterialColor();
  const { formatMass } = useMassFormatter();
  const { t } = useTenantTranslation();

  const layerBackground = (layer: DisplayLayer): string => {
    if (layer.components.length < 2) {
      return getColor(layer.materialName);
    }
    const stripeStops = layer.components
      .map(
        (component, index) =>
          `${getColor(component.materialName)} ${index * STRIPE_WIDTH_PX}px ${
            (index + 1) * STRIPE_WIDTH_PX
          }px`
      )
      .join(", ");
    return `repeating-linear-gradient(45deg, ${stripeStops})`;
  };

  const layerTooltip = (layer: DisplayLayer): React.ReactNode =>
    layer.components.length < 2 ? (
      t("layerTooltip", {
        material: layer.materialName,
        mass: formatMass(layer.mass),
      })
    ) : (
      <Box>
        {layer.components.map((component) => (
          <Typography
            key={component.materialName}
            variant="caption"
            component="div"
          >
            {t("layerTooltip", {
              material: component.materialName,
              mass: formatMass(component.mass),
            })}
          </Typography>
        ))}
      </Box>
    );

  const bottomToTop = sorted(layers, (layer) => layer.layerNumber);
  const total = bottomToTop.reduce((sum, layer) => sum + layer.mass, 0);
  // scaleMass is the mass that fills the whole bucket (referenceMass / fill
  // fraction), shared across every column so they stay comparable. A column whose
  // total exceeds it would stack past the bucket rim, so we clamp it and flag it.
  const isOverflowing = scaleMass > 0 && total > scaleMass;
  // A layer is fully hidden once the layers below it already fill the bucket. Walking
  // from the bottom, we test the mass accumulated *below* each layer before adding its
  // own, so the layer straddling the rim (still partly visible) is not listed. Only the
  // fully hidden layers go in the overflow tooltip so their mass stays legible.
  const hiddenLayers = isOverflowing
    ? bottomToTop.reduce<{ cumulative: number; hidden: DisplayLayer[] }>(
        (acc, layer) => ({
          cumulative: acc.cumulative + layer.mass,
          hidden:
            acc.cumulative > scaleMass ? [...acc.hidden, layer] : acc.hidden,
        }),
        { cumulative: 0, hidden: [] }
      ).hidden
    : [];

  return (
    <Stack direction="column" alignItems="center" width={200} gap={0.5}>
      <Typography
        variant="h4"
        sx={{ fontWeight: "fontWeightBold" }}
        textAlign="center"
      >
        {title}
      </Typography>
      {bottomToTop.length === 0 ? (
        <>
          {/* Placeholder mass line keeps the empty box aligned with populated
              columns, which show their total mass above the box. */}
          <Typography variant="body2" color="text.secondary">
            {t("noValuePlaceholder")}
          </Typography>
          <Box
            sx={{
              width: "100%",
              height: containerHeight,
              border: `2px dashed ${theme.palette.grey[400]}`,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: theme.palette.grey[100],
            }}
          >
            <Typography variant="body2" color="text.disabled">
              {t("noLayerData")}
            </Typography>
          </Box>
        </>
      ) : (
        <>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {formatMass(total)}
            </Typography>
            {isOverflowing ? (
              <Tooltip
                arrow
                title={
                  <Box>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ fontWeight: "fontWeightMedium" }}
                    >
                      {t("overflowingBucket")}
                    </Typography>
                    {hiddenLayers.map((layer) => (
                      <Typography
                        key={layer.layerNumber}
                        variant="caption"
                        component="div"
                      >
                        {t("layerTooltip", {
                          material: layer.materialName,
                          mass: formatMass(layer.mass),
                        })}
                      </Typography>
                    ))}
                  </Box>
                }
              >
                <WarningAmberRoundedIcon fontSize="small" color="error" />
              </Tooltip>
            ) : null}
          </Stack>
          <Box
            sx={{
              width: "100%",
              height: containerHeight,
              border: `2px solid ${
                isOverflowing
                  ? theme.palette.error.main
                  : theme.palette.grey[400]
              }`,
              borderRadius: 1,
              overflow: isOverflowing ? "hidden" : "visible",
              display: "flex",
              flexDirection: "column-reverse",
            }}
          >
            {bottomToTop.map((layer) => {
              const layerHeight =
                scaleMass > 0 ? (layer.mass / scaleMass) * containerHeight : 0;
              const formattedMass = formatMass(layer.mass);
              return (
                <Tooltip
                  key={layer.layerNumber}
                  arrow
                  placement="left"
                  title={layerTooltip(layer)}
                >
                  <Box
                    sx={{
                      height: layerHeight,
                      minHeight: 2,
                      flexShrink: 0,
                      background: layerBackground(layer),
                      borderTop: `1px solid ${theme.palette.grey[800]}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      px: 0.5,
                    }}
                  >
                    {layerHeight >= INLINE_LABEL_MIN_HEIGHT ? (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{
                          maxWidth: "100%",
                          px: 0.5,
                          borderRadius: 0.5,
                          bgcolor: alpha(theme.palette.background.paper, 0.85),
                          color: "text.primary",
                        }}
                      >
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontWeight: "fontWeightMedium",
                            lineHeight: 1.2,
                          }}
                        >
                          {layer.materialName}
                        </Typography>
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{
                            fontWeight: "fontWeightMedium",
                            lineHeight: 1.2,
                          }}
                        >
                          {formattedMass}
                        </Typography>
                      </Stack>
                    ) : null}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </>
      )}
    </Stack>
  );
};
